import { isFunction, uniqBy } from 'lodash';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { centerMapToPort } from '@/components/views/port-insights/map/utils';
import { fluentProvider } from '@/lib/api/providers/fluent';
import { AIRPORT, SEAPORT } from '@/lib/constants';
import { portInsightsMapInstanceState } from '@/state/map';
import {
  carriersIsOpenState,
  hoveredPortState,
  isSearchingAllPortsState,
  isSearchingPortsState,
  isSearchingRoutesSummaryState,
  locationAutocompleteValueState,
  portsFiltersCarriersState,
  portsSearchParamsState,
  portsState,
  selectedPortDetailsState,
  selectedPortsState,
  showConnectionsState,
} from '@/state/ports';
import { PortInterface, PortsUrlQueryParams } from '@/types';
import { PortApiInterface } from '@/types/api-types';
import { getPreferredIdentifier } from '@/utils/helpers';
import { logEvent } from '@/utils/logger';
import { getLocationUrlQueryValue } from '@/utils/places';
import { trackUser } from '@/utils/tracking';

import { useNotifications } from './use-notifications';
import { useRouterQuery } from './use-router-query';

export interface UsePortsInterface {
  performInitialPortsSearch?: ({
    portFromUrlParam,
    placeFromUrlParam,
  }: {
    portFromUrlParam?: string | null;
    placeFromUrlParam?: string | null;
  }) => void;
  performPortsSearch?: (params?: PortsUrlQueryParams) => void;
  performPortLookup?: (value: PortInterface) => void;
  performRouteSummarySearch?: ({ pol, pod }: { pol: PortInterface; pod: PortInterface }) => void;
  handleNearbyListingClose?: () => void;
  getRouteSummary?: ({ pol, pod }: { pod: PortInterface; pol: PortInterface }) => void;
}

export const usePorts = (): UsePortsInterface => {
  const setPorts = useSetRecoilState(portsState);
  const resetPorts = useResetRecoilState(portsState);
  const [isSearchingPorts, setIsSearchingPorts] = useRecoilState(isSearchingPortsState);
  const setIsSearchingRouteSummary = useSetRecoilState(isSearchingRoutesSummaryState);
  const [isSearchingAllPorts, setIsSearchingAllPorts] = useRecoilState(isSearchingAllPortsState);
  const setSelectedPorts = useSetRecoilState(selectedPortsState);
  const setSelectedPortDetails = useSetRecoilState(selectedPortDetailsState);
  const setShowConnections = useSetRecoilState(showConnectionsState);
  const setCarriersIsOpen = useSetRecoilState(carriersIsOpenState);
  const setHoveredPort = useSetRecoilState(hoveredPortState);
  const mapInstance = useRecoilValue(portInsightsMapInstanceState);
  const setPortsCarriers = useSetRecoilState(portsFiltersCarriersState);
  const setSearchParams = useSetRecoilState(portsSearchParamsState);
  const setLocationAutocompleteValue = useSetRecoilState(locationAutocompleteValueState);

  const { getUrlQueryParam, addUrlQueryParam, removeUrlQueryParam } = useRouterQuery();
  const { addNotification } = useNotifications();

  // Get all ports from API
  const performInitialPortsSearch = async ({
    portFromUrlParam,
    placeFromUrlParam,
  }: {
    portFromUrlParam?: string | null;
    placeFromUrlParam?: string | null;
  }) => {
    if (isSearchingAllPorts) return;

    // Check if there is a port param in the url
    const portNameUrlParam = portFromUrlParam || getUrlQueryParam('port');
    const placeNameUrlParam = placeFromUrlParam || getUrlQueryParam('place');

    setIsSearchingAllPorts(true);

    try {
      const { ports } = await fluentProvider.getPorts();
      const { carriers } = await fluentProvider.getAllCarriers();

      if (portNameUrlParam) {
        // Split the identifier string into type and value (e.g. "iata LAX" -> ["iata", "LAX"])
        const [identifierType, identifierValue] = portNameUrlParam.split(' ');

        // Lookup the port by identifier
        const portFromData = ports.find((port: PortInterface) => {
          const identifier = getPreferredIdentifier(port.ids);
          return identifier && identifier.type === identifierType && identifier.value === identifierValue;
        });

        // If there is a port param, perform a port lookup
        if (portFromData) {
          await performPortLookup(portFromData);
        }
      }

      if (placeNameUrlParam) {
        // This will trigger the list opening and the map centering
        setLocationAutocompleteValue(placeNameUrlParam);
      }

      replacePortsResults(ports);

      if (carriers) setPortsCarriers(carriers);
    } catch (error: any) {
      logEvent.error('Port Insights Fetch All Ports', {
        error,
        functionName: 'performInitialPortsSearch',
      });

      addNotification({
        heading: 'Error fetching ports',
        description: 'Try reloading the page.',
        variant: 'error',
      });
    } finally {
      setIsSearchingAllPorts(false);
    }
  };

  /**
   * Perform a search for ports based on the given params
   * Used as a basic version of the above function
   */
  const performPortsSearch = async (params?: PortsUrlQueryParams) => {
    if (isSearchingAllPorts) return;

    setIsSearchingAllPorts(true);

    try {
      const { ports } = await fluentProvider.getPorts(params);

      replacePortsResults(ports);
    } catch (error: any) {
      logEvent.error('Port Insights Fetch Ports', {
        error,
        functionName: 'performPortsSearch',
      });
      addNotification({
        heading: 'Error fetching ports',
        description: 'Try reloading the page.',
        variant: 'error',
      });
    } finally {
      setIsSearchingAllPorts(false);
    }
  };

  // Get selected port
  const performPortLookup = async (port: PortInterface) => {
    if (isSearchingPorts) return;

    setIsSearchingPorts(true);

    try {
      const result = await fluentProvider.getPortById(port.id);

      setSelectedPorts({
        origin: port,
        destination: null,
      });

      setShowConnections(true);
      setCarriersIsOpen(false);
      setSelectedPortDetails(result);
      setHoveredPort(null);

      if (mapInstance) {
        centerMapToPort(port, mapInstance);
      }

      // Add the port to the URL query string
      const portUrlQueryValue = getLocationUrlQueryValue(port);
      addUrlQueryParam('port', portUrlQueryValue);
    } catch (error: any) {
      logEvent.error('Port Insights Fetch Port', {
        error,
        functionName: 'performPortLookup',
      });
      addNotification({
        heading: 'Error fetching port details',
        description: "Please try again, if that doesn't work, try reloading the page.",
        variant: 'error',
      });
    } finally {
      setIsSearchingPorts(false);
    }
  };

  const performRouteSummarySearch = async ({ pol, pod }: { pol: PortInterface; pod: PortInterface }) => {
    setIsSearchingRouteSummary(true);

    try {
      const polPreferredIdentifier = getPreferredIdentifier(pol?.ids);
      const podPreferredIdentifier = getPreferredIdentifier(pod?.ids);

      const result = await fluentProvider.getRouteSummary({
        pol: `${polPreferredIdentifier?.type} ${polPreferredIdentifier?.value}`,
        pod: `${podPreferredIdentifier?.type} ${podPreferredIdentifier?.value}`,
      });

      return setSelectedPorts((prev) => {
        return {
          origin: prev?.origin || null,
          destination: prev?.destination || null,
          routeSummary: result,
        };
      });
    } catch (error: any) {
      logEvent.error('Route Summary Search', {
        error,
        origin: pol,
        destination: pod,
        functionName: 'performRouteSummarySearch',
      });
      addNotification({
        heading: 'Error fetching route summary',
        description: "Please try again, if that doesn't work, try reloading the page.",
        variant: 'error',
      });
    } finally {
      setIsSearchingRouteSummary(false);
    }

    return null;
  };

  // Replaces Ports results in Ports state
  const replacePortsResults = (ports: PortApiInterface[]) => {
    if (ports && ports.length) {
      const uniquePorts = uniqBy(ports, 'name');
      // Filter out any Seaports without Carriers
      // AND filter out any Airports with Frequency
      const filteredPorts = uniquePorts.filter(
        (port) =>
          (port.type === SEAPORT && (port.carrierCount || 0) > 0) ||
          (port.type === AIRPORT && (port.frequency || 0) > 0),
      );

      setPorts(filteredPorts);
    } else {
      resetPorts();
    }
  };

  const handleNearbyListingClose = () => {
    setSearchParams((prev) => ({ ...prev, origin: null }));
    setLocationAutocompleteValue('');

    // Analytics Event
    trackUser.event('Close Nearby Ports');

    removeUrlQueryParam('place');
  };

  const getRouteSummary = async ({ pol, pod }: { pol: PortInterface; pod: PortInterface }) => {
    if (isFunction(performRouteSummarySearch)) {
      return performRouteSummarySearch({ pol, pod });
    }

    return null;
  };

  return {
    performInitialPortsSearch,
    performPortsSearch,
    performPortLookup,
    performRouteSummarySearch,
    handleNearbyListingClose,
    getRouteSummary,
  };
};
